import { AutoSizeText } from "@-/dev.improve.haniverse.components.ui.text.auto-size";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { MorphemeMnemonicsMeaningViewContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { Platform, Pressable, Text, View } from "@-/com.npmjs.react-native";
import { Fragment, useContext } from "react";
import type { MorphemeMnemonicsMeaningViewTitleSectionGate } from "#use-morpheme-mnemonics-meaning-view-title-section-gate";

/**
 * Title size at full scale. `AutoSizeText` only goes below this when the back cannot wrap into
 * two lines at this size.
 */
const TITLE_FONT_SIZE = 26;

/**
 * A tighter line box than `leading-normal` (1.5×) so a wrapped two-line heading stays one
 * visual unit rather than reading as stacked separate titles.
 */
const TITLE_LINE_HEIGHT = 30;

function titleAppearsInMnemonic(title: string, mnemonic: string | null): boolean {
  const normalizedTitle = title.normalize("NFKC").trim().toLocaleLowerCase("en");
  const normalizedMnemonic = mnemonic?.normalize("NFKC").toLocaleLowerCase("en");

  if (!normalizedMnemonic || normalizedTitle.length === 0) {
    return false;
  }

  return normalizedMnemonic.includes(normalizedTitle);
}

export function MorphemeMnemonicsMeaningViewTitleSectionRenderer({
  gate,
}: {
  gate: MorphemeMnemonicsMeaningViewTitleSectionGate;
}) {
  const { title } = gate;
  const [final, mnemonic, grapheme, mnemonicTerms, label] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.final,
    (c) => c.readonlySlice.data.mnemonic,
    (c) => c.readonlySlice.data.grapheme,
    (c) => c.readonlySlice.data.mnemonicTerms,
    (c) => c.readonlySlice.data.label,
  );
  const { openCompoundsSheet, openMnemonicUsagesSheet } = useContext(
    MorphemeMnemonicsMeaningViewContext,
  );

  const backAppearsInMnemonic = titleAppearsInMnemonic(title, mnemonic);
  const linksToCompounds = final !== true && backAppearsInMnemonic;
  // The family precedes the weight: `AutoSizeText` merges classes, and a weight listed before an
  // arbitrary `font-[…]` family is dropped as a conflicting font utility.
  const mnemonicTermClassName = Platform.select({
    android: "font-[sans-serif] font-normal italic",
    ios: "font-[System] font-normal italic",
    web: "font-normal italic",
  });
  const mnemonicTitleClassName = Platform.select({
    android: "font-[sans-serif] font-bold italic",
    ios: "font-[System] font-bold italic",
    web: "font-bold italic",
  });
  const isMnemonicLabel = label?.trim() === "mnemonic";
  // A mnemonic label makes the title itself the sole italic heading.
  const mnemonicHeadingTerms =
    mnemonicTerms.length > 0
      ? final === true
        ? mnemonicTerms.filter((term) => term !== title)
        : mnemonicTerms
      : final !== true && backAppearsInMnemonic
        ? [title]
        : [];
  const showMnemonic = !isMnemonicLabel && mnemonicHeadingTerms.length > 0;
  // An authored mnemonic that repeats the back keeps the two apart: the plain bold gloss, then
  // every mnemonic term (the repeated one included) on its own line beneath it.
  const splitsMnemonicTerms =
    showMnemonic && mnemonicTerms.length > 0 && mnemonicHeadingTerms.includes(title);
  // A back that merely recurs in the mnemonic sentence is styled as its mnemonic in place.
  const titleIsMnemonicTerm = showMnemonic && mnemonicHeadingTerms.includes(title);
  const aliasTerms = showMnemonic ? mnemonicHeadingTerms.filter((term) => term !== title) : [];
  // Authored component mnemonics (not a back that merely recurs in the mnemonic) link to the
  // mnemonics that use them.
  const mnemonicTermsPressProps =
    final !== true && mnemonicTerms.length > 0
      ? {
          accessibilityHint: `Shows the mnemonics that use ${grapheme} as a component`,
          accessibilityRole: "link" as const,
          onPress: openMnemonicUsagesSheet,
          suppressHighlighting: true,
        }
      : undefined;
  // Every pressable mnemonic in the heading is underlined so the reader knows it can be tapped —
  // authored terms open their usages, and a back re-used in the mnemonic opens the compounds sheet
  // through the surrounding Pressable. The bold gloss itself stays plain.
  const mnemonicTermsUnderlineClassName =
    mnemonicTermsPressProps !== undefined || linksToCompounds ? "underline" : "";
  const trimmedLabel = label?.trim() ?? "";
  // Centred under the glyph: the label sits between the glyph and the heading it qualifies.
  const titleText = (
    <View className="w-full min-w-0 items-center gap-1">
      {trimmedLabel === "" ? null : (
        <Text
          className="text-[10px] leading-4 font-medium tracking-widest text-muted-foreground uppercase"
          testID="meaning-label"
        >
          {trimmedLabel}
        </Text>
      )}
      {/* Long backs wrap before shrinking and are never ellipsized. Title + aliases share one
          AutoSizeText so they stay the same size and wrap as a single heading. */}
      {isMnemonicLabel ? (
        <AutoSizeText
          className={`text-center ${mnemonicTermClassName} ${linksToCompounds ? "underline" : ""}`}
          colorVariant="default"
          fontSize={TITLE_FONT_SIZE}
          lineHeight={TITLE_LINE_HEIGHT}
          maxLines={2}
          minimumFontScale={0.55}
        >
          {title}
        </AutoSizeText>
      ) : splitsMnemonicTerms ? (
        // One AutoSizeText keeps both lines the same size; each may wrap once before it shrinks.
        <AutoSizeText
          className="text-center"
          colorVariant="default"
          fontSize={TITLE_FONT_SIZE}
          lineHeight={TITLE_LINE_HEIGHT}
          maxLines={3}
          minimumFontScale={0.55}
        >
          <Text className="font-bold">{title}</Text>
          {"\n"}
          <Text testID="meaning-title-mnemonic-terms">
            {mnemonicHeadingTerms.map((term, index) => (
              <Fragment key={term}>
                {index === 0 ? null : <Text>, </Text>}
                <Text
                  {...mnemonicTermsPressProps}
                  className={`${mnemonicTermClassName} ${mnemonicTermsUnderlineClassName}`}
                >
                  {term}
                </Text>
              </Fragment>
            ))}
          </Text>
        </AutoSizeText>
      ) : showMnemonic ? (
        <AutoSizeText
          className="text-center"
          colorVariant="default"
          fontSize={TITLE_FONT_SIZE}
          lineHeight={TITLE_LINE_HEIGHT}
          maxLines={2}
          minimumFontScale={0.55}
        >
          {titleIsMnemonicTerm ? (
            <Text
              {...mnemonicTermsPressProps}
              className={`${mnemonicTitleClassName} ${mnemonicTermsUnderlineClassName}`}
            >
              {title}
            </Text>
          ) : (
            <Text className="font-bold">{title}</Text>
          )}
          {aliasTerms.length > 0 ? (
            <>
              {/* Unstyled separator — neither bold like the back nor italic like the mnemonic. */}
              <Text>, </Text>
              <Text testID="meaning-title-mnemonic-terms">
                {aliasTerms.map((term, index) => (
                  <Fragment key={term}>
                    {index === 0 ? null : <Text>, </Text>}
                    <Text
                      {...mnemonicTermsPressProps}
                      className={`${mnemonicTermClassName} ${mnemonicTermsUnderlineClassName}`}
                    >
                      {term}
                    </Text>
                  </Fragment>
                ))}
              </Text>
            </>
          ) : null}
        </AutoSizeText>
      ) : (
        <AutoSizeText
          className="text-center font-bold"
          colorVariant="default"
          fontSize={TITLE_FONT_SIZE}
          lineHeight={TITLE_LINE_HEIGHT}
          maxLines={2}
          minimumFontScale={0.55}
        >
          {title}
        </AutoSizeText>
      )}
    </View>
  );

  return linksToCompounds ? (
    <Pressable
      accessibilityLabel={`Show compounds using ${grapheme}`}
      accessibilityRole="button"
      className="w-full min-w-0"
      hitSlop={8}
      onPress={openCompoundsSheet}
    >
      {titleText}
    </Pressable>
  ) : (
    titleText
  );
}
